import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    Input,
    Label,
    FormGroup,
    Row,
    Col,
    Badge,
    Alert,
    Spinner
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";

interface NewClient {
    prenom: string;
    nom: string;
    societe: string;
    codePostal: string;
    telephone: string;
}

interface Client {
    rowid: string;
    nom: string;
    address?: string;
    email?: string;
    phone?: string;
    outstanding_limit?: string;
    town?: string | null;
    zip?: string | null;
}

interface ClientSelectionModalProps {
    isOpen: boolean;
    toggle: () => void;
    onConfirm: (clientName: string, rowid?: string, newClientInfo?: NewClient) => void;
}

const ClientSelectionModal: React.FC<ClientSelectionModalProps> = ({
    isOpen,
    toggle,
    onConfirm,
}) => {
    const { user } = useAuth();

    const [selectedClientType, setSelectedClientType] = useState<"compte" | "comptoir" | "stock" | "new" | "document" | "">("");
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [newClient, setNewClient] = useState<NewClient>({
        prenom: "",
        nom: "",
        societe: "",
        codePostal: "",
        telephone: ""
    });
    const [clientSearchTerm, setClientSearchTerm] = useState("");
    const [fetchedClients, setFetchedClients] = useState<Client[]>([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientFetchError, setClientFetchError] = useState<string | null>(null);

    // States for document search
    const [searchType, setSearchType] = useState<string>('avoir');
    const [documentNumber, setDocumentNumber] = useState<string>('');
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<Client[]>([]);
    const [documentSearchError, setDocumentSearchError] = useState<string | null>(null);

    const documentTypes = [
        { value: 'avoir', label: 'Avoir' },
        { value: 'commande', label: 'Commande' },
        { value: 'facture', label: 'Facture' },
        { value: 'immat', label: 'Immatriculation' }
    ];

    const fetchClients = async (searchTerm: string) => {
        if (
            searchTerm.trim().length < 3 ||
            selectedClientType === "comptoir" ||
            selectedClientType === "stock"
        ) {
            setFetchedClients([]);
            return;
        }

        setLoadingClients(true);
        setClientFetchError(null);

        try {
            const response = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "clients",
                    entity: user.entity,
                    search: searchTerm,
                    encompte: selectedClientType === "compte" ? true : false
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            const clients: Client[] = response.data || [];
            setFetchedClients(clients);
        } catch (error) {
            setClientFetchError("Erreur lors de la récupération des clients.");
        } finally {
            setLoadingClients(false);
        }
    };

    useEffect(() => {
        if (selectedClient) return;
        const delayDebounceFn = setTimeout(() => {
                fetchClients(clientSearchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [clientSearchTerm, selectedClientType, user.entity, user.token]);

    const handleDocumentSearch = async () => {
        if (!documentNumber.trim()) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        setDocumentSearchError(null);

        try {
            const response = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "searchSoc",
                    entity: user.entity,
                    term: documentNumber,
                    type: searchType,
                },
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`
                    }
                }
            );

            if (response.data) {
                const transformedClients: Client[] = response.data.map((item: any) => ({
                    rowid: item.id || "",
                    nom: item.nom || item.name || "",
                    phone: item.phone || "",
                    email: item.email || "",
                    town: item.town || "",
                    zip: item.zip || ""
                }));
                setSearchResults(transformedClients);
            } else {
                setSearchResults([]);
                setDocumentSearchError("Aucun résultat trouvé.");
            }
        } catch (error) {
            setDocumentSearchError("Erreur lors de la recherche par document.");
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelectClientFromDocument = (client: Client) => {
        setSelectedClient(client);
        setSelectedClientType("document");
        setSearchResults([]);
        setDocumentNumber('');
        setFetchedClients([]);
    };

    const handleSelectClient = (client: Client) => {
        setSelectedClient(client);
        setSelectedClientType("compte");
        setClientSearchTerm(client.nom);
        setFetchedClients([]);
    };

    const handleClearSelectedClient = () => {
        setSelectedClient(null);
        setSelectedClientType("");
        setClientSearchTerm("");
        setDocumentNumber("");
        setSearchResults([]);
        setFetchedClients([]);
        setNewClient({
            prenom: "",
            nom: "",
            societe: "",
            codePostal: "",
            telephone: ""
        });
    };

    const handleConfirm = () => {
        if (selectedClientType === "new") {
            let displayName = "";

            if (newClient.prenom || newClient.nom) {
                displayName = `${newClient.prenom} ${newClient.nom}`.trim();
            } else {
                displayName = newClient.societe || "";
            }

            onConfirm(
                displayName,
                undefined,
                newClient
            );
        } else if (selectedClientType === "comptoir") {
            onConfirm("Client Comptoir", "74057");
        } else if (selectedClientType === "stock") {
            // ID du  Stock défini à 9999999999
            onConfirm(" Stock", "9999999999");
        } else if (selectedClient) {
            onConfirm(selectedClient.nom, selectedClient.rowid);
        }

        toggle();
        resetForm();
    };

    const resetForm = () => {
        setSelectedClientType("");
        setSelectedClient(null);
        setNewClient({ prenom: "", nom: "", societe: "", codePostal: "", telephone: "" });
        setClientSearchTerm("");
        setDocumentNumber("");
        setFetchedClients([]);
        setSearchResults([]);
    };

    const isClientSelected = !!selectedClient;

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
            <ModalHeader toggle={toggle}>
                {selectedClient ? `Client sélectionné :` : "Sélectionner un client"}
            </ModalHeader>
            <ModalBody>
                {selectedClient && (
                    <div className="mb-3 p-3 border rounded bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{ width: 40, height: 40 }}>
                                    <i className="ri-user-line"></i>
                                </div>
                                <div>
                                    <h5 className="mb-0">{selectedClient.nom}</h5>
                                    {selectedClient.phone && (
                                        <small className="text-muted">
                                            <i className="ri-phone-line me-1"></i>
                                            {selectedClient.phone}
                                        </small>
                                    )}
                                    <div className="small text-muted">
                                        {selectedClientType === "compte" && "Client en compte"}
                                        {selectedClientType === "document" && "Trouvé par recherche document"}
                                        {selectedClientType === "comptoir" && "Client comptoir"}
                                        {selectedClientType === "stock" && " Stock"}
                                    </div>
                                </div>
                            </div>
                            <Button
                                color="link"
                                className="text-danger p-0"
                                onClick={handleClearSelectedClient}
                            >
                                <i className="ri-close-circle-fill ri-lg"></i>
                            </Button>
                        </div>
                    </div>
                )}

                <div className="d-flex gap-4">
                    {/* Left Box - Clients APA */}
                    <div className="w-50 p-3 border rounded">
                        <h5 className="mb-3 text-primary">Clients APA</h5>

                        <div className="d-flex gap-3 mb-3">
                            <div className="form-check">
                                <Input
                                    type="checkbox"
                                    id="compte"
                                    name="clientType"
                                    checked={selectedClientType === "compte"}
                                    onChange={() => {
                                        if (!isClientSelected) {
                                            setSelectedClientType(prev =>
                                                prev === "compte" ? "" : "compte"
                                            );
                                            setSelectedClient(null);
                                            setFetchedClients([]);
                                            setSearchResults([]);
                                            setDocumentNumber("");
                                        }
                                    }}
                                    className="form-check-input"
                                    disabled={isClientSelected}
                                />
                                <Label for="compte" className="form-check-label">En Compte</Label>
                            </div>

                            <div className="form-check">
                                <Input
                                    type="checkbox"
                                    id="comptoir"
                                    name="clientType"
                                    checked={selectedClientType === "comptoir"}
                                    onChange={() => {
                                        if (!isClientSelected) {
                                            setSelectedClientType(prev =>
                                                prev === "comptoir" ? "" : "comptoir"
                                            );
                                            setSelectedClient(null);
                                            setFetchedClients([]);
                                            setSearchResults([]);
                                            setDocumentNumber("");
                                        }
                                    }}
                                    className="form-check-input"
                                    disabled={isClientSelected}
                                />
                                <Label for="comptoir" className="form-check-label">Client Comptoir</Label>
                            </div>

                            {/* Case à cocher Stock */}
                            <div className="form-check">
                                <Input
                                    type="checkbox"
                                    id="stock"
                                    name="clientType"
                                    checked={selectedClientType === "stock"}
                                    onChange={() => {
                                        if (!isClientSelected) {
                                            setSelectedClientType(prev =>
                                                prev === "stock" ? "" : "stock"
                                            );
                                            setSelectedClient(null);
                                            setFetchedClients([]);
                                            setSearchResults([]);
                                            setDocumentNumber("");
                                        }
                                    }}
                                    className="form-check-input"
                                    disabled={isClientSelected}
                                />
                                <Label for="stock" className="form-check-label">Stock</Label>
                            </div>
                        </div>

                        {selectedClientType !== "comptoir" && selectedClientType !== "stock" && (
                            <>
                                <Input
                                    type="text"
                                    placeholder="Rechercher un client..."
                                    className="mb-2 form-control-md"
                                    value={clientSearchTerm}
                                    onChange={(e) => !isClientSelected && setClientSearchTerm(e.target.value)}
                                    disabled={isClientSelected}
                                    onFocus={() => {
                                        if (!isClientSelected && fetchedClients.length > 0) {
                                            setFetchedClients([]);
                                        }
                                    }}
                                />

                                {loadingClients && <p className="small">Chargement des clients...</p>}
                                {clientFetchError && <p className="text-danger small">{clientFetchError}</p>}

                                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                                    {fetchedClients.map((client) => (
                                        <div
                                            key={client.rowid}
                                            className={`p-2 mb-1 border rounded cursor-pointer ${selectedClient?.rowid === client.rowid ? 'bg-primary text-white' : ''}`}
                                            onClick={() => !isClientSelected && handleSelectClient(client)}
                                            style={{ cursor: isClientSelected ? 'not-allowed' : 'pointer' }}
                                        >
                                            <div className="fw-medium">{client.nom}</div>
                                            {client.phone && (
                                                <div className={`small ${selectedClient?.rowid === client.rowid ? 'text-white-50' : 'text-muted'}`}>
                                                    <i className="ri-phone-line me-1"></i>
                                                    {client.phone}
                                                </div>
                                            )}
                                            {client.email && (
                                                <div className={`small ${selectedClient?.rowid === client.rowid ? 'text-white-50' : 'text-muted'}`}>
                                                    <i className="ri-mail-line me-1"></i>
                                                    {client.email}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Document Search Section */}
                        <div className="mt-4 pt-3 border-top">
                            <h5 className="mb-3 text-danger">
                                <i className="ri-file-search-line me-1"></i>
                                Recherche par document
                            </h5>

                            <Row className="align-items-end g-2 mb-3">
                                <Col xs={6}>
                                    <FormGroup className="mb-0">
                                        <Label for="searchType" className="form-label small mb-1">Type</Label>
                                        <Input
                                            type="select"
                                            id="searchType"
                                            value={searchType}
                                            onChange={(e) => !isClientSelected && setSearchType(e.target.value)}
                                            className="form-control-sm"
                                            style={{ fontSize: '11px' }}
                                            disabled={isClientSelected || selectedClientType === "comptoir" || selectedClientType === "stock"}
                                        >
                                            {documentTypes.map(type => (
                                                <option
                                                    key={type.value}
                                                    value={type.value}
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>

                                <Col xs={4}>
                                    <FormGroup className="mb-0">
                                        <Label for="documentNumber" className="form-label small mb-1">Numéro</Label>
                                        <Input
                                            type="text"
                                            id="documentNumber"
                                            placeholder={`Numéro...`}
                                            value={documentNumber}
                                            onChange={(e) => !isClientSelected && setDocumentNumber(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !isClientSelected && selectedClientType !== "comptoir" && selectedClientType !== "stock") {
                                                    handleDocumentSearch();
                                                }
                                            }}
                                            className="form-control"
                                            style={{ fontSize: '11px' }}
                                            disabled={isClientSelected || selectedClientType === "comptoir" || selectedClientType === "stock"}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col xs={2}>
                                    <FormGroup className="mb-0">
                                        <Label className="form-label small mb-1 d-block">&nbsp;</Label>
                                        <Button
                                            color="danger"
                                            onClick={handleDocumentSearch}
                                            disabled={searchLoading || !documentNumber.trim() || isClientSelected || selectedClientType === "comptoir" || selectedClientType === "stock"}
                                            className="btn-sm w-100"
                                            style={{ height: '35px' }}
                                        >
                                            {searchLoading ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                <i className="ri-search-line"></i>
                                            )}
                                        </Button>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {searchLoading && (
                                <div className="text-center py-2">
                                    <Spinner size="sm" className="me-2" />
                                    <small>Recherche en cours...</small>
                                </div>
                            )}

                            {!searchLoading && searchResults.length > 0 && (
                                <div className="mt-2">
                                    <div className="d-flex align-items-center mb-2">
                                        <Badge color="danger" className="me-2">
                                            {searchResults.length}
                                        </Badge>
                                        <small className="text-muted">
                                            Client(s) trouvé(s) pour {searchType}: {documentNumber}
                                        </small>
                                    </div>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {searchResults.map((client) => (
                                            <div
                                                key={client.rowid}
                                                className={`p-2 mb-1 border rounded cursor-pointer ${selectedClient?.rowid === client.rowid ? 'bg-danger text-white' : 'bg-light'}`}
                                                onClick={() => !isClientSelected && handleSelectClientFromDocument(client)}
                                                style={{ cursor: isClientSelected ? 'not-allowed' : 'pointer' }}
                                            >
                                                <div className="fw-medium small">{client.nom}</div>
                                                {client.phone && (
                                                    <div className={`small ${selectedClient?.rowid === client.rowid ? 'text-white-50' : 'text-muted'}`}>
                                                        <i className="ri-phone-line me-1"></i>
                                                        {client.phone}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!searchLoading && documentSearchError && (
                                <Alert color="info" className="py-1 mt-2 small">
                                    <i className="ri-information-line me-1"></i>
                                    {documentSearchError}
                                </Alert>
                            )}
                        </div>
                    </div>

                    {/* Right Box - Nouveau Client */}
                    <div className="w-50 p-3 border rounded">
                        <h5 className="mb-3 text-success">Nouveau Client</h5>

                        <div className="form-check mb-3">
                            <Input
                                type="checkbox"
                                id="new"
                                name="clientType"
                                checked={selectedClientType === "new"}
                                onChange={() => {
                                    if (!isClientSelected) {
                                        setSelectedClientType(prev =>
                                            prev === "new" ? "" : "new"
                                        );
                                        setSelectedClient(null);
                                        setClientSearchTerm("");
                                        setFetchedClients([]);
                                        setSearchResults([]);
                                        setDocumentNumber("");
                                    }
                                }}
                                className="form-check-input"
                                disabled={isClientSelected}
                            />
                            <Label for="new" className="form-check-label">Créer un nouveau client</Label>
                        </div>

                        <div className="row">
                            <FormGroup className="mb-2 col-md-6">
                                <Label>Prénom</Label>
                                <Input
                                    className="form-control-md"
                                    value={newClient.prenom}
                                    onChange={(e) => !isClientSelected && setNewClient({ ...newClient, prenom: e.target.value })}
                                    placeholder="Entrez le prénom"
                                    onFocus={() => !isClientSelected && setSelectedClientType("new")}
                                    disabled={isClientSelected}
                                />
                            </FormGroup>

                            <FormGroup className="mb-2 col-md-6">
                                <Label>Nom</Label>
                                <Input
                                    className="form-control-md"
                                    value={newClient.nom}
                                    onChange={(e) => !isClientSelected && setNewClient({ ...newClient, nom: e.target.value })}
                                    placeholder="Entrez le nom"
                                    onFocus={() => !isClientSelected && setSelectedClientType("new")}
                                    disabled={isClientSelected}
                                />
                            </FormGroup>
                        </div>

                        <FormGroup className="mb-2">
                            <Label>Société</Label>
                            <Input
                                className="form-control-md"
                                value={newClient.societe}
                                onChange={(e) => !isClientSelected && setNewClient({ ...newClient, societe: e.target.value })}
                                placeholder="Entrez la société"
                                onFocus={() => !isClientSelected && setSelectedClientType("new")}
                                disabled={isClientSelected}
                            />
                        </FormGroup>

                        <FormGroup className="mb-2">
                            <Label>Code Postal</Label>
                            <Input
                                className="form-control-md"
                                value={newClient.codePostal}
                                onChange={(e) => !isClientSelected && setNewClient({ ...newClient, codePostal: e.target.value })}
                                placeholder="Entrez le code postal"
                                onFocus={() => !isClientSelected && setSelectedClientType("new")}
                                disabled={isClientSelected}
                            />
                        </FormGroup>

                        <FormGroup className="mb-2">
                            <Label>Téléphone</Label>
                            <Input
                                className="form-control-md"
                                value={newClient.telephone}
                                onChange={(e) => !isClientSelected && setNewClient({ ...newClient, telephone: e.target.value })}
                                placeholder="Entrez le téléphone"
                                onFocus={() => !isClientSelected && setSelectedClientType("new")}
                                disabled={isClientSelected}
                            />
                        </FormGroup>
                    </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        color="primary"
                        onClick={handleConfirm}
                        disabled={
                            (!selectedClientType && !selectedClient) ||
                            (selectedClientType === "new" && (!newClient.prenom && !newClient.societe)) ||
                            ((selectedClientType === "compte" || selectedClientType === "document") && !selectedClient)
                        }
                    >
                        Confirmer
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default ClientSelectionModal;